// Matter Assistant — LAW-001 v0.1. Same posture as the FIN-001 assistant:
// capability-complete on corporate practice, information-starved on the bundle.
// It reasons only over what the candidate pastes or explicitly shares.
import { harrowDocuments } from '../data/harrowDocuments'
import { AssistantReply } from './aiEngine'

const has = (text: string, terms: string[]) => terms.some((t) => text.includes(t))

const SHARED_LABEL: Record<string, string> = harrowDocuments.reduce(
  (acc, d) => ({ ...acc, [d.id]: d.title }),
  {} as Record<string, string>,
)

function sharedList(sharedDocs: string[]) {
  return sharedDocs.map((id) => `${id} (${SHARED_LABEL[id] ?? 'shared document'})`).join(', ')
}

// The assistant's view of the seller question depends entirely on whether the
// register of members has been put in front of it. That asymmetry is the point.
function sellerAnswer(sharedDocs: string[]): string {
  const seesRegister = sharedDocs.includes('HAR-ROM-03')
  const seesDisclosure = sharedDocs.includes('HAR-DL-02')

  if (seesRegister || seesDisclosure) {
    return [
      'That changes my earlier read, and I should be direct about why.',
      '',
      `On what I can now see, the seller named in the agreement and the transferor on the stock transfer form are both **Vale Nominees Ltd**, but the registered holder of the shares is **Vale Nominees (No. 2) Ltd**. Those are two different registered names, and on the face of the documents they are two different legal entities.`,
      '',
      'What I cannot tell you is whether that matters. There are ordinary explanations — a nominee arrangement, an intra-group transfer that has been agreed but not registered, or simply the wrong entity name typed into the transfer form. Each of those has a different consequence, and the documents in front of me do not distinguish between them.',
      '',
      'What I would say with confidence: a transfer executed by a party that is not the registered holder is not something to resolve by assumption. This is the kind of point a supervising lawyer would expect to be raised before completion rather than after it.',
    ].join('\n')
  }

  return [
    'On the documents I have been given, the seller information is internally consistent.',
    '',
    '**Vale Nominees Ltd** is named as Seller in the agreement and as transferor on the stock transfer form, and the share count and consideration agree between them. There is nothing in what you have shared that contradicts that.',
    '',
    'One limit worth stating plainly: consistency between the agreement and the transfer form only tells you those two documents say the same thing. It does not tell you the named party actually holds the shares — that is established by the register of members, which I have not seen. If you have it, share it and I will check the two against each other.',
  ].join('\n')
}

export function generateLegalReply(
  rawInput: string,
  priorPrompts: number,
  sharedDocs: string[] = [],
): AssistantReply {
  const text = rawInput.toLowerCase()
  const lines = rawInput.split('\n').filter((l) => l.trim().length > 0)
  const pastedData = lines.length > 3 || rawInput.length > 320

  const asksToSee = has(text, [
    'look at the',
    'open the',
    'can you see',
    'read the document',
    'the bundle',
    'the tab',
    'the file',
    'pull up',
    'the matter',
    'all the documents',
  ])

  if (asksToSee && sharedDocs.length === 0 && !pastedData) {
    return {
      pastedData,
      content: [
        'I cannot see the matter bundle. I have no access to the documents tab, the bundle review or the instructions — only what you paste into this chat or share with me explicitly.',
        '',
        'Use the “Share with assistant” action on any document and I will work from it. Tell me what you want tested and I will be specific about what the document does and does not establish.',
      ].join('\n'),
    }
  }

  // Seller / transferor identity — the flagship asymmetry.
  if (
    has(text, [
      'seller',
      'transferor',
      'vale',
      'shareholder',
      'registered holder',
      'who owns',
      'nominee',
      'entity',
    ])
  ) {
    return { pastedData, content: sellerAnswer(sharedDocs) }
  }

  // Execution / deeds.
  if (has(text, ['execut', 'deed', 'signature', 'signed', 'witness', 'attest', 'signatory'])) {
    const seesSpa = sharedDocs.includes('HAR-SPA-01')
    if (seesSpa) {
      return {
        pastedData,
        content: [
          'Taking the agreement’s own execution provision as the starting point rather than any general rule:',
          '',
          'Clause 22.2 states that where Ashworth Capital Ltd executes by a **single director**, that signature must be made in the presence of a witness who attests it. The execution block for Ashworth is drafted for exactly that route — “acting by a director in the presence of:”.',
          '',
          'On the page as shared, the director has signed. The witness signature, witness name and witness address are all blank. There is no second signatory block for Ashworth anywhere on the page, so the two-signatory alternative is not the route being used here.',
          '',
          'So the route the document selects for itself has not been completed on its face. Whether that is fatal or simply needs finishing before completion is a call for the person supervising the matter — but it is not something to sign off on as it stands.',
        ].join('\n'),
      }
    }
    return {
      pastedData,
      content: [
        'Execution as a deed is worth checking properly, because it is one of the few places where a document can be substantively correct and still not do its job.',
        '',
        'The practical test is narrow: find the execution provision in the agreement, see which route it contemplates for each party, then turn to the execution page and check that route has actually been completed. A witnessed single-director signature needs the witness block filled in; a two-signatory route needs both signatures present.',
        '',
        'I cannot do that from the body of an agreement alone — the defect, if there is one, lives on the execution page. Share the agreement including its execution page and I will compare the two.',
      ].join('\n'),
    }
  }

  // Consent / lease / change of control.
  if (has(text, ['consent', 'landlord', 'lease', 'change of control', 'manchester', 'premises'])) {
    return {
      pastedData,
      content: [
        'Change-of-control consent is a condition to watch because it sits across two or three documents that are rarely read together.',
        '',
        'The lease or property schedule tells you whether consent is required. The disclosure letter tells you the current status of the application. The deal instructions tell you whether the consent is required *before* completion or merely to be pursued. It is quite common for the first and third to be satisfied while the second is still outstanding, and nothing in a single document flags that.',
        '',
        'If consent is required before completion and has not been obtained, that is a matter for the supervising lawyer to decide on — the options are usually to defer completion, obtain a waiver, or complete with the risk expressly accepted by the client. That decision is not a trainee’s to make. Share the property schedule and disclosure letter and I will set out what each actually says.',
      ].join('\n'),
    }
  }

  // Completion date / recency.
  if (has(text, ['completion date', 'date', '9 october', '10 october', 'checklist', 'stale', 'conflict'])) {
    const seesInstruction = sharedDocs.includes('HAR-CI-05')
    if (seesInstruction) {
      return {
        pastedData,
        content: [
          'With the later instruction in front of me the apparent conflict resolves.',
          '',
          'The client confirmed on 7 October that completion moved from 9 October to 10 October, and asked for the completion documents and checklist to be updated. The agreement already records 10 October, so the agreement is current. The checklist still shows 9 October, so the checklist is simply out of date.',
          '',
          'That makes this a document housekeeping point rather than a substantive term in dispute: update the checklist, and note that the instruction is the authority for the change. Worth confirming no other document keyed off the 9 October date.',
        ].join('\n'),
      }
    }
    return {
      pastedData,
      content: [
        'Where two documents in a bundle give different dates, the question is almost never which document is more authoritative in the abstract — it is which one is **most recent**, and whether something later in the file explains the change.',
        '',
        'Before treating a date conflict as a drafting error, look for a later instruction, email or amendment. A checklist is a working document and goes stale quickly; an agreement is usually updated deliberately. If a later instruction moved the date, the conflict is administrative and the fix is to update the stale document.',
        '',
        'I only see what you share with me, so I cannot tell you whether such an instruction exists in this bundle. If there is correspondence about the completion date, share it.',
      ].join('\n'),
    }
  }

  // Price / consideration.
  if (has(text, ['price', 'consideration', 'purchase price', '4,200,000', '4.2m', 'completion statement'])) {
    return {
      pastedData,
      content: [
        'For consideration, the check is a three-way agreement: the figure in the instructions, the figure in the agreement’s consideration clause, and the figure carried into the completion statement and the transfer form.',
        '',
        'On what you have shared those agree at £4,200,000, with no adjustments recorded on the completion statement. That is a clean line — one confirming check against each source is proportionate, and there is no reason to keep returning to it.',
        '',
        'The only thing I would add: if the completion statement showed adjustments and the agreement had no adjustment mechanic, that would be worth a query. It does not.',
      ].join('\n'),
    }
  }

  // Warranty limitations.
  if (has(text, ['warrant', 'cap', 'limitation', 'term sheet', 'schedule 5', 'de minimis', 'claims'])) {
    return {
      pastedData,
      content: [
        'Warranty limitations are checked line against line, not in summary — cap, per-claim de minimis, general notification period, tax notification period.',
        '',
        'On the material shared, Schedule 5 gives an aggregate cap of £840,000, an 18-month general notification period, 7 years for tax claims and a £10,000 de minimis. Those correspond to the agreed term sheet on every limb.',
        '',
        'That is a verified match. Re-checking it repeatedly adds nothing — record that you agreed it against the term sheet and move on.',
      ].join('\n'),
    }
  }

  // Board minutes / conflict — the decoy.
  if (has(text, ['board', 'minutes', 'conflict', 'interest', 'director', 'quorum', 'article'])) {
    return {
      pastedData,
      content: [
        'A declared interest in board minutes looks like a governance point but usually resolves within the minutes themselves. The sequence to check is short: was the interest declared, do the articles permit an interested director to count in the quorum and vote, and was the resolution recorded.',
        '',
        'On what you have shared, all three are present — the declaration is recorded, the articles are cited as permitting participation, and the approval is minuted. That is a properly constituted approval.',
        '',
        'I would note it as checked and move on. A declared and permitted interest is not a defect, and spending the remaining time here has an opportunity cost against the parts of the bundle that do not reconcile.',
      ].join('\n'),
    }
  }

  // Review the bundle / check against instructions.
  if (has(text, ['check these', 'against the deal', 'against the instructions', 'review', 'anything wrong', 'discrepanc', 'issues'])) {
    if (sharedDocs.length === 0 && !pastedData) {
      return {
        pastedData,
        content:
          'Happy to — but I need the documents. Share them with me or paste the text, and tell me what the instructions actually require. Without the instructions I can tell you what a document says, not whether it says the right thing.',
      }
    }
    return {
      pastedData,
      content: [
        `I have worked through what you shared${sharedDocs.length ? ` — ${sharedList(sharedDocs)}` : ''}.`,
        '',
        '1. **Consideration** — the price agrees between the sources provided. No adjustment mechanic in play.',
        '2. **Parties** — the agreement and the transfer form name the same seller, and the share count agrees.',
        '3. **Warranty limitations** — cap and time limits correspond to the agreed commercial position.',
        '4. **Conditions** — the change-of-control consent requirement is identified in the documents provided.',
        '5. **Execution** — the agreement contains an execution mechanic appropriate to the parties.',
        '',
        'On the evidence in front of me, nothing in these documents contradicts anything else in them.',
        '',
        'I would put one caveat on that, because it matters more than the conclusion: I have only reviewed what you shared. Consistency across a subset of a bundle is not the same as consistency across the bundle. Anything establishing a fact that these documents merely *assert* — registers, consents, execution pages, later correspondence — would need to be checked separately.',
      ].join('\n'),
    }
  }

  // Summarise the SPA.
  if (has(text, ['summar', 'key terms', 'spa', 'agreement', 'draft', 'structure'])) {
    return {
      pastedData,
      content: [
        'A first-pass structure for a review note that a supervising associate can act on:',
        '',
        '1. **Scope** — which documents you reviewed, and which you did not.',
        '2. **Agreed items** — terms you checked against instructions and found supported, with the source named.',
        '3. **Items requiring attention** — each with the documents that establish it, and what you propose.',
        '4. **Items you could not resolve** — what evidence is missing, and what you would ask for.',
        '5. **Recommendation** — whether the matter is ready to complete, ready subject to the items raised, or not.',
        '',
        'Be explicit in (1) and (4). A reviewer reads the boundary of your review as carefully as its content, and “I did not see the register” is a far better answer than an unqualified conclusion drawn without it.',
      ].join('\n'),
    }
  }

  if (priorPrompts === 0) {
    return {
      pastedData,
      content: [
        'I am the Matter Assistant. Two things up front:',
        '',
        '1. I know corporate practice, transaction mechanics and document review well.',
        '2. **I cannot see the matter bundle.** I only know what you paste here or share with me explicitly.',
        '',
        'So my answers are bounded by the evidence you put in front of me. If I confirm something looks consistent, that means consistent *within what I have seen* — which is not the same thing as correct. Share documents and ask specific questions.',
      ].join('\n'),
    }
  }

  return {
    pastedData,
    content: [
      'I can help with that, with one limit stated plainly: I cannot see the bundle review, the instructions or the documents tab — only what you share or paste here.',
      '',
      'Give me the document plus the specific question — does this term match the instruction, do these two documents agree, has this condition been satisfied — and I will work it through and tell you where the evidence runs out.',
    ].join('\n'),
  }
}
